import React, { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { inter, philosopher } from "@/lib/fonts";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  IPaginatedResponse,
  IWalletContext,
  IWalletTransaction,
  WalletTransactionType,
  defaultPaginationOptions,
  priceFormat,
} from "muminsmart-utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { getWalletTransactions } from "@/lib/api/wallet";
import BankDetailDialog from "@/app/wallet/BankDetailDialog";
import WithdrawDrawer from "@/app/wallet/WithdrawDrawer";
import { toast } from "sonner";
import { useCreateWalletWithdrawalMutation } from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import AppPagination from "@/app/components/common/AppPagination";
import { WalletTransactionsSkeleton } from "../components/common/skeletons/WalletSkeleton";
import { footerBg3Style, wallet } from "@/assets";
import DateRangePicker from "@/app/components/common/DateRangePicker";

interface WalletViewProps {
  walletContexts: IWalletContext | null;
}

export default function WalletView({ walletContexts }: WalletViewProps) {
  const [createWalletWithdrawal] = useCreateWalletWithdrawalMutation();
  const [activeContextId, setActiveContextId] = useState<string>("");
  const [transactions, setTransactions] = useState<IWalletTransaction[]>([]);
  const [transactionMeta, setTransactionMeta] = useState<
    IPaginatedResponse<IWalletTransaction>["meta"] | null
  >(null);
  const [transactionPage, setTransactionPage] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isBankDialogOpen, setIsBankDialogOpen] = useState(false);
  const [isWithdrawDrawerOpen, setIsWithdrawDrawerOpen] = useState(false);
  const [bankDetailsSavedSellerIds, setBankDetailsSavedSellerIds] = useState<
    string[]
  >([]);
  const [bankDialogDismissedSellerIds, setBankDialogDismissedSellerIds] =
    useState<string[]>([]);

  const contextOptions = useMemo(() => {
    const options: {
      id: string;
      label: string;
      balance: number;
      sellerId?: string;
      isSeller: boolean;
      hasBankDetails?: boolean;
    }[] = [];

    walletContexts?.sellers?.forEach((seller, index) => {
      options.push({
        id: seller.walletId,
        label: seller.sellerName?.trim() || `Seller ${index + 1}`,
        balance: Number(seller.balance ?? 0),
        sellerId: seller.sellerId,
        isSeller: true,
        hasBankDetails: seller.hasBankDetails,
      });
    });

    if (walletContexts?.user) {
      options.push({
        id: walletContexts.user.walletId,
        label: "Personal",
        balance: Number(walletContexts.user.balance ?? 0),
        isSeller: false,
        hasBankDetails: walletContexts.user.hasBankDetails,
      });
    }

    return options;
  }, [walletContexts]);

  useEffect(() => {
    if (!contextOptions.some((option) => option.id === activeContextId)) {
      setActiveContextId(contextOptions[0]?.id ?? "");
      setTransactionPage(1);
    }
  }, [activeContextId, contextOptions]);

  // Fetch paginated transactions when wallet context, page, or date range changes
  useEffect(() => {
    if (!activeContextId) return;

    const fetchTransactions = async () => {
      setIsLoadingTransactions(true);
      try {
        const params: Parameters<typeof getWalletTransactions>[1] = {
          ...defaultPaginationOptions,
          page: transactionPage,
        };
        if (dateRange?.from) {
          params.startDate = dateRange.from.toISOString().split("T")[0];
          params.endDate = (dateRange.to ?? dateRange.from)
            .toISOString()
            .split("T")[0];
        }

        const response = await getWalletTransactions(activeContextId, params);

        if (response.success && response.data) {
          setTransactions(response.data.items ?? []);
          setTransactionMeta(response.data.meta ?? null);
        } else {
          setTransactions([]);
          setTransactionMeta(null);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        setTransactions([]);
        setTransactionMeta(null);
      } finally {
        setIsLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [activeContextId, contextOptions, transactionPage, dateRange]);

  useEffect(() => {
    setTransactionPage(1);
  }, [activeContextId]);

  const activeContext = useMemo(
    () => contextOptions.find((option) => option.id === activeContextId),
    [activeContextId, contextOptions],
  );

  useEffect(() => {
    if (!activeContext || !activeContext.isSeller || !activeContext.sellerId) {
      setIsBankDialogOpen(false);
      return;
    }

    const alreadySaved = bankDetailsSavedSellerIds.includes(
      activeContext.sellerId,
    );
    const dismissedForNow = bankDialogDismissedSellerIds.includes(
      activeContext.sellerId,
    );

    if (!activeContext.hasBankDetails && !alreadySaved && !dismissedForNow) {
      setIsBankDialogOpen(true);
    } else {
      setIsBankDialogOpen(false);
    }
  }, [activeContext, bankDetailsSavedSellerIds, bankDialogDismissedSellerIds]);

  const balance = Number(activeContext?.balance ?? 0);

  const lastRedeemed = useMemo(() => {
    const latestDebit = [...transactions]
      .filter((t) => t.type === WalletTransactionType.Debit)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0];

    if (!latestDebit) return null;

    return new Date(latestDebit.createdAt).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [transactions]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    setTransactionPage(1);
  };

  const activeSellerId = activeContext?.isSeller
    ? activeContext.sellerId
    : undefined;

  const handleRedeemNow = async () => {
    if (!activeContext) {
      toast.error("Wallet context not found");
      return;
    }

    if (!activeContext.hasBankDetails) {
      toast.error("Please add bank details before withdrawing");
      return;
    }

    setIsWithdrawDrawerOpen(true);
  };

  const handleWithdrawConfirm = async (amount: number) => {
    if (!activeContext) {
      toast.error("Wallet context not found");
      return;
    }

    try {
      await createWalletWithdrawal({
        walletId: activeContextId,
        amount,
        description: "Wallet withdrawal",
      }).unwrap();

      toast.success(`Redeem request submitted for ${priceFormat(amount)}`);

      const refreshParams: Parameters<typeof getWalletTransactions>[1] = {
        ...defaultPaginationOptions,
        page: transactionPage,
      };
      if (dateRange?.from) {
        refreshParams.startDate = dateRange.from.toISOString().split("T")[0];
        refreshParams.endDate = (dateRange.to ?? dateRange.from)
          .toISOString()
          .split("T")[0];
      }
      const refreshed = await getWalletTransactions(
        activeContextId,
        refreshParams,
      );
      if (refreshed.success && refreshed.data) {
        setTransactions(refreshed.data.items ?? []);
        setTransactionMeta(refreshed.data.meta ?? null);
      }

    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to withdraw amount"));
    }
  };

  return (
    <>
      <WithdrawDrawer
        open={isWithdrawDrawerOpen}
        onOpenChange={setIsWithdrawDrawerOpen}
        contextLabel={activeContext?.label ?? "Wallet"}
        availableBalance={Number(activeContext?.balance ?? 0)}
        onConfirm={handleWithdrawConfirm}
      />

      <BankDetailDialog
        open={isBankDialogOpen}
        walletId={activeContextId}
        onSaved={() => {
          if (!activeSellerId) return;

          setBankDetailsSavedSellerIds((prev) =>
            prev.includes(activeSellerId) ? prev : [...prev, activeSellerId],
          );
          setIsBankDialogOpen(false);
        }}
        onDismiss={() => {
          if (!activeSellerId) {
            setIsBankDialogOpen(false);
            return;
          }

          setBankDialogDismissedSellerIds((prev) =>
            prev.includes(activeSellerId) ? prev : [...prev, activeSellerId],
          );
          setIsBankDialogOpen(false);
        }}
      />

      <div className="px-4 space-y-5">
        {/* Balance Card */}
        <div className="flex items-center gap-4 rounded-full border border-amber-200 p-5 bg-linear-to-r from-yellow-100 to-amber-200">
          <img
            src={wallet}
            alt="Wallet"
            width={88}
            height={88}
            className="shrink-0"
          />
          <div className="flex flex-col gap-1.5 min-w-0">
            <p
              className={`text-xs tracking-widest text-[#6F6F6F] font-semibold uppercase ${inter.className}`}
            >
              Current Balance
            </p>
            <p
              className={`text-4xl font-bold text-secondary leading-none ${philosopher.className}`}
            >
              {priceFormat(activeContext?.balance ?? balance)}
            </p>
            <span className="inline-flex items-center self-start bg-white/80 border border-gray-200 rounded-full px-3 py-0.5 text-xs text-gray-500 whitespace-nowrap">
              {lastRedeemed ? (
                <>
                  Last redeemed on&nbsp;
                  <span className="text-[#F2994A] font-medium">
                    {lastRedeemed}
                  </span>
                </>
              ) : (
                "No redemptions yet"
              )}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`flex gap-3 ${inter.className}`}>
          <Button
            className="flex-1 rounded-full bg-secondary text-white h-12 text-sm font-semibold hover:bg-secondary/90"
            onClick={handleRedeemNow}
          >
            Redeem Now
          </Button>
        </div>
        {/* <div className={`flex gap-2 overflow-x-auto pb-1 ${inter.className}`}>
          {contextOptions.map((option) => {
            const isActive = option.id === activeContextId;
            return (
              <button
                key={option.id}
                onClick={() => setActiveContextId(option.id)}
                className={cn(
                  "shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "border-secondary bg-secondary text-white"
                    : "border-gray-200 bg-white text-gray-700",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div> */}

        {contextOptions.length > 1 ? (
          <div className={`flex flex-wrap ${inter.className}`}>
            <RadioGroup
              defaultValue="comfortable"
              className="flex w-max min-w-full flex-row items-center gap-6 whitespace-nowrap"
              value={activeContextId}
              onValueChange={(value) => setActiveContextId(value)}
            >
              {contextOptions.map((option) => {
                return (
                  <div className="flex items-center gap-3" key={option.id}>
                    <RadioGroupItem
                      value={option.id}
                      id={`option-${option.id}`}
                    />
                    <Label htmlFor={`option-${option.id}`}>
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        ) : null}
      </div>

      {/* Transactions */}
      <div
        className="mt-4 min-h-[calc(100vh-13.5rem)] rounded-t-3xl bg-white p-6 pb-27 lg:mx-auto"
        style={footerBg3Style}
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            className={`text-xl font-bold text-secondary ${philosopher.className}`}
          >
            Transactions
          </h2>

          <DateRangePicker
            label=""
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>

        {/* Transaction List */}
        <div className="space-y-3">
          {isLoadingTransactions ? <WalletTransactionsSkeleton count={3} /> : null}
          {!isLoadingTransactions && transactions.length === 0 && (
            <p
              className={`text-center text-gray-400 py-10 text-sm ${inter.className}`}
            >
              No transactions found
            </p>
          )}
          {!isLoadingTransactions &&
            transactions.length > 0 &&
            transactions.map((tx) => {
              const isCredit = tx.type === WalletTransactionType.Credit;

              return (
                <div
                  key={tx.id}
                  className="flex items-center justify-between bg-white rounded-xl p-4 border border-gray-100 shadow-sm"
                >
                  <div className={`space-y-0.5 ${inter.className} min-w-0`}>
                    <p className="text-sm text-gray-600">
                      From{" "}
                      <span className="font-bold text-gray-900">
                        {tx.referenceType?.toUpperCase()}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {tx.description}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {new Date(tx.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "font-semibold text-sm ml-4 shrink-0",
                      isCredit ? "text-secondary" : "text-red-500",
                    )}
                  >
                    {isCredit ? "+" : "-"}{" "}
                    {Number(tx.amount).toLocaleString("en-IN")}
                  </span>
                </div>
              );
            })}
        </div>

        {!!transactionMeta && transactionMeta.totalPages > 1 && (
          <div className={`mt-4 ${inter.className}`}>
            <AppPagination
              currentPage={transactionMeta.currentPage}
              totalPages={transactionMeta.totalPages}
              isLoading={isLoadingTransactions}
              onPageChange={setTransactionPage}
            />
          </div>
        )}
      </div>
    </>
  );
}
